import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { 
    FaFileAlt, 
    FaArrowLeft, 
    FaSort,
    FaPrint,
    FaEye,
    FaLoader
} from 'react-icons/fa';

const EngineerSubheInterestedWorksList = () => {
    const navigate = useNavigate();
    const [department, setDepartment] = useState('subhe'); // Default to subhe as per menu
    const [taluka, setTaluka] = useState('');
    const [works, setWorks] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [selectedTaluka, setSelectedTaluka] = useState('');
    const [loading, setLoading] = useState(true);

    // Marathi numerals for Sr.No.
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    const toMarathiNumber = (n) => String(n).split('').map(digit => marathiNumerals[parseInt(digit)] || digit).join('');

    useEffect(() => {
        setLoading(true);

        let engData = {};
        let socData = {};
        let engDone = false;
        let socDone = false;

        const checkLoading = () => {
            if (engDone && socDone) setLoading(false);
        };

        const checkRegDone = () => {
            if (engDone && socDone) {
                const allRegs = [
                    ...Object.entries(engData).map(([key, val]) => ({ ...val, _key: key, _node: 'ekamwatapzp/EngineerRegistrations' })),
                    ...Object.entries(socData).map(([key, val]) => ({ ...val, _key: key, _node: 'ekamwatapzp/SocietyRegistrations' }))
                ];
                setRegistrations(allRegs);
                checkLoading();
            }
        };

        // Fetch All Works
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        const unsubWorks = onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                let flattenedWorks = [];
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        if (val.schemeName) {
                            flattenedWorks.push({ id: key, meetingNumber: val.meetingNumber || "General", ...val });
                        } else {
                            Object.entries(val).forEach(([id, value]) => {
                                flattenedWorks.push({ id, meetingNumber: key, ...value });
                            });
                        }
                    }
                });
                setWorks(flattenedWorks);
            }
            checkLoading();
        });

        // Fetch Registrations (Both Engineer and Society)
        const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

        const unsubEng = onValue(engRef, (snapshot) => {
            engData = snapshot.val() || {};
            engDone = true;
            checkRegDone();
        });

        const unsubSoc = onValue(socRef, (snapshot) => {
            socData = snapshot.val() || {};
            socDone = true;
            checkRegDone();
        });

        return () => {
            unsubWorks();
            unsubEng();
            unsubSoc();
        };
    }, []);

    const talukas = [
        "मिरज", "जत", "खानापूर (विटा)", "वाळवा (इस्लामपूर)", "तासगाव", 
        "शिराळा", "कडेगाव", "खानापूर", "पलूस", "कवठे महांकाळ"
    ];

    // Filter and Process Data
    const filteredWorks = useMemo(() => {
        const deptMap = { 'subhe': 'सुबे', 'society': 'मजूर' };
        return works.filter(work => {
            const deptMatch = !department || work.sharedwork === deptMap[department];
            const talukaMatch = !selectedTaluka || work.taluka === selectedTaluka;
            return deptMatch && talukaMatch;
        });
    }, [works, department, selectedTaluka]);

    // Pre-calculate work interest counts from registrations for O(1) lookup
    const workInterestMap = useMemo(() => {
        const counts = {};
        registrations.forEach(reg => {
            if (reg.work_payment) {
                Object.entries(reg.work_payment).forEach(([meetingNum, paymentData]) => {
                    const selected = paymentData.selectedWorks || [];
                    selected.forEach(workStr => {
                        // Extract workNumber from "WORKID - Work Name..."
                        const workNum = workStr.split(' - ')[0];
                        const key = `${meetingNum}_${workNum}`;
                        counts[key] = (counts[key] || 0) + 1;
                    });
                });
            }
        });
        return counts;
    }, [registrations]);

    const displayData = useMemo(() => {
        return filteredWorks.map(work => ({
            ...work,
            interestedCount: workInterestMap[`${work.meetingNumber}_${work.workNumber}`] || 0
        }));
    }, [filteredWorks, workInterestMap]);

    const handleSearch = () => {
        setSelectedTaluka(taluka);
    };

    const totalInterested = displayData.reduce((acc, curr) => acc + curr.interestedCount, 0);

    return (
        <EngineerLayout>
            <div className="p-8 bg-[#f4f7fe] min-h-screen font-marathi">
                {/* Header Section */}
                <div className="flex justify-between items-center mb-8 px-2">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate('/engineer/subhe-interested-works-menu')}
                            className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition text-gray-600 border border-gray-100 transition-transform active:scale-90"
                        >
                            <FaArrowLeft size={16} />
                        </button>
                        <h1 className="text-xl font-bold text-gray-800 tracking-tight">सुभे इंटरेस्टेड कामांची यादी पहा</h1>
                    </div>
                    <div className="text-xs font-medium text-gray-400">
                        जिल्हा परिषद / <span className="text-gray-500 font-bold tracking-tight">सुभे इंटरेस्टेड कामांची यादी पहा</span>
                    </div>
                </div>

                {/* Filter Card */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-500">विभाग <span className="text-red-500">*</span></label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-blue-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                            >
                                <option value="">विभाग प्रकार निवडा</option>
                                <option value="subhe">सु. बे.</option>
                                <option value="society">समाज</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[13px] font-bold text-gray-500">तालुका निवडा</label>
                            <select 
                                className="w-full border border-gray-100 rounded-xl px-4 py-3 outline-none focus:border-blue-500 bg-gray-50/50 text-gray-700 font-bold transition-all"
                                value={taluka}
                                onChange={(e) => setTaluka(e.target.value)}
                            >
                                <option value="">तालुका निवडा</option>
                                {talukas.map((t, idx) => (
                                    <option key={idx} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <button 
                                onClick={handleSearch}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/20 transition transform active:scale-95 flex items-center justify-center gap-2"
                            >
                                शोध
                            </button>
                        </div>
                    </div>

                    <div className="mt-10">
                        {/* Table Section */}
                        <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm bg-white">
                            {loading ? (
                                <div className="p-20 flex flex-col items-center justify-center space-y-4">
                                    <div className="animate-spin text-blue-600 text-4xl">
                                        <FaSort />
                                    </div>
                                    <p className="text-gray-400 font-bold animate-pulse uppercase tracking-widest text-xs">माहिती लोड होत आहे...</p>
                                </div>
                            ) : (
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-white border-b border-gray-200">
                                            <th className="px-6 py-5 text-[12px] font-black text-gray-800 text-left border-r border-gray-100 uppercase w-16">
                                                #
                                            </th>
                                            <th className="px-6 py-5 text-[12px] font-black text-gray-800 text-left border-r border-gray-100 uppercase min-w-[150px]">
                                                कामाचा आयडी
                                            </th>
                                            <th className="px-6 py-5 text-[12px] font-black text-gray-800 text-left border-r border-gray-100 uppercase min-w-[350px]">
                                                कामाचे नाव
                                            </th>
                                            <th className="px-6 py-5 text-[12px] font-black text-gray-800 text-left border-r border-gray-100 uppercase min-w-[180px]">
                                                निविदा रक्कम
                                            </th>
                                            <th className="px-6 py-5 text-[12px] font-black text-gray-800 text-left uppercase min-w-[250px]">
                                                इंटरेस्टेड काउंट
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayData.length > 0 ? displayData.map((item, index) => (
                                            <tr key={item.id} className="hover:bg-blue-50/20 group transition-colors border-b border-gray-100 last:border-0 text-[14px] text-gray-700 font-bold">
                                                <td className="px-6 py-6 border-r border-gray-100">{toMarathiNumber(index + 1)}</td>
                                                <td className="px-6 py-6 border-r border-gray-100 font-marathi">{item.workNumber}</td>
                                                <td className="px-6 py-6 border-r border-gray-100 font-marathi leading-relaxed">{item.workName}</td>
                                                <td className="px-6 py-6 border-r border-gray-100 font-marathi">{item.tenderAmount}</td>
                                                <td className="px-6 py-6">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-gray-800">{toMarathiNumber(item.interestedCount)}</span>
                                                        <span className="text-gray-300">-</span>
                                                        <button 
                                                            onClick={() => navigate(`/engineer/subhe-interested-works-detail/${item.id}`)}
                                                            className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2 group/link"
                                                        >
                                                            <FaEye className="text-blue-400 group-hover/link:scale-110 transition-transform" />
                                                            <span className="font-marathi underline decoration-blue-200 underline-offset-4">
                                                                इंटरेस्टेड ॲप्लिकेशन पहा
                                                            </span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-20 text-center text-gray-400 font-bold">
                                                    कोणतीही माहिती उपलब्ध नाही.
                                                </td>
                                            </tr>
                                        )}
                                        {/* Footer Totals Row */}
                                        <tr className="bg-gray-50/50">
                                            <td colSpan="4" className="px-6 py-6 text-right border-r border-gray-100 font-black text-gray-700 tracking-tight">
                                                एकूण इंटरेस्टेड संख्या
                                            </td>
                                            <td className="px-6 py-6 font-black text-gray-800">
                                                {toMarathiNumber(totalInterested)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Print Button */}
                        <div className="mt-12 flex justify-center">
                            <button 
                                onClick={() => window.print()}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/20 transition transform active:scale-95 flex items-center gap-3"
                            >
                                <FaPrint />
                                प्रिंट
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .font-marathi {
                    font-family: 'Hind', sans-serif;
                }
                @import url('https://fonts.googleapis.com/css2?family=Hind:wght@400;500;600;700&display=swap');
                @media print {
                    .no-print { display: none; }
                    body { background: white; }
                    .p-8 { padding: 0; }
                }
            `}</style>
        </EngineerLayout>
    );
};

export default EngineerSubheInterestedWorksList;
